<template>
    <AppModal grid size="large" :backdrop="true">
        <AppModalContent>
            <AppModalBody class="!px-0" v-click-outside="handleCloseModal">
                <div class="flex flex-col h-full overflow-y-auto py-4 !px-6 !min-h-[60vh]">
                    <div class="flex items-center justify-between mb-2">
                        <div class="flex items-center gap-4">
                            <AppModalHeader
                                headingText="Canned responses"
                                classes="*:!font-bold !px-0 !py-4"
                                @onHeaderToggle="handleCloseModal"
                            />
                        </div>
                        <div class="cursor-pointer">
                            <fa
                                :icon="['fal', 'gear']"
                                class="text-[24px] text-gray-300 font-normal"
                                @click="() => null"
                            />
                        </div>
                    </div>

                    <div
                        v-if="!loadingStates.list"
                        class="bg-white rounded-lg max-h-full overflow-auto grid grid-cols-6 gap-10 flex-1"
                    >
                        <div class="col-span-2">
                            <div class="">
                                <div class="w-full">
                                    <div class="relative w-full mb-2">
                                        <FormKit
                                            type="search"
                                            @input="searchDebounced"
                                            placeholder="Search"
                                            inner-class="bg-[#F1F5F7] rounded-full outline-none pl-7 overflow-hidden"
                                            outer-class="!mb-3"
                                        />
                                        <fa
                                            :icon="['fas', 'search']"
                                            class="text-gray-300 absolute left-3 bottom-3 w-4 h-4"
                                        />
                                    </div>
                                </div>
                                <div
                                    v-if="cannedResponses.fetch_by_count?.length"
                                    class="border-b-[1px] border-b-gray-200 pb-3 mb-3 overflow-auto"
                                >
                                    <div>
                                        <h3 class="text-gray-300 uppercase font-bold text-xs p-2 mb-2.5">
                                            Frequently Used
                                        </h3>
                                    </div>
                                    <div
                                        v-for="(i, index) in cannedResponses.fetch_by_count"
                                        :key="index"
                                        class="flex flex-col"
                                    >
                                        <div
                                            @click="() => setActiveCannedResponse(i)"
                                            :class="
                                                activeCannedResponses.hasOwnProperty('count') &&
                                                i.id === activeCannedResponses.id
                                                    ? 'text-sm font-normal leading-4 italic text-[#2B2C30] items-center flex py-[11px] gap-1 bg-[rgb(241,245,247)] pl-2 rounded-lg mb-0 cursor-pointer'
                                                    : 'text-sm font-normal leading-4 italic text-[#2B2C30] mx-1 py-3 pl-1 mb-0 cursor-pointer'
                                            "
                                        >
                                            <fa
                                                v-if="
                                                    activeCannedResponses.hasOwnProperty('count') &&
                                                    i.id === activeCannedResponses.id
                                                "
                                                :icon="['far', 'chevron-right']"
                                                class="text-[#00B0FC] w-3 h-[18px]"
                                            />
                                            {{ i.title }}
                                        </div>
                                    </div>
                                </div>
                                <div v-else class="text-center font-medium text-sm mt-4">
                                    <fa :icon="['fas', 'empty-set']" class="text-sm text-gray-600 mr-1" />
                                    <span> No Data Available </span>
                                </div>
                                <div class="mt-2 overflow-auto" v-if="cannedResponses.list?.length">
                                    <div v-for="(i, index) in cannedResponses.list" :key="index" class="flex flex-col">
                                        <div
                                            @click="() => setActiveCannedResponse(i)"
                                            :class="
                                                !activeCannedResponses.hasOwnProperty('count') &&
                                                i.id === activeCannedResponses.id
                                                    ? 'text-sm font-normal leading-4 italic text-[#2B2C30] items-center flex py-[11px] gap-1 bg-[rgb(241,245,247)] pl-2 rounded-lg mb-0 cursor-pointer'
                                                    : 'text-sm font-normal leading-4 italic text-[#2B2C30] mx-2 py-3 pl-1 mb-0 cursor-pointer'
                                            "
                                        >
                                            <fa
                                                v-if="
                                                    !activeCannedResponses.hasOwnProperty('count') &&
                                                    i.id === activeCannedResponses.id
                                                "
                                                :icon="['far', 'chevron-right']"
                                                class="text-[#00B0FC] w-3 h-[18px]"
                                            />
                                            {{ i.title }}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div class="col-span-4" v-if="activeCannedResponses.detail_info">
                            <div class="flex flex-col justify-between h-full">
                                <div class="h-full overflow-auto canned_message">
                                    <Receiver
                                        :chat="{ contentType: 'text', content: activeCannedResponses.detail_info }"
                                        :isBottomHidden="true"
                                    />
                                </div>
                                <div class="flex justify-end">
                                    <app-button
                                        class="flex items-center !h-8"
                                        variant="btn-blue"
                                        @btnClick="handleSelectCannedResponse"
                                    >
                                        <fa :icon="['fas', 'plus']" class="text-sm mr-1" />
                                        <span class="text-sm font-medium">Use canned response</span>
                                    </app-button>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div v-else class="flex mt-6 justify-center items-center gap-2">
                        <div class="w-6 h-6 border-4 border-t-blue-500 border-gray-300 rounded-full animate-spin"></div>
                        <span class="font-semibold text-sm">Loading ...</span>
                    </div>
                </div>
            </AppModalBody>
        </AppModalContent>
    </AppModal>
</template>

<script setup>
import { AppModal, AppModalBody, AppModalContent, AppModalHeader } from "@/components/appComponents/modal";
import { getCannedResponseList } from "@/api/SettingServices.js";
import Receiver from "../Receiver.vue";
import AppButton from "@/components/AppButton.vue";
import { onMounted, ref } from "vue";
import { useDebounceFn } from "@vueuse/core";

const emits = defineEmits(["handleCloseModal", "handleSelectCannedResponse"]);

// [info]: states
const cannedResponses = ref({});
const activeCannedResponses = ref({});
const loadingStates = ref({
    list: false,
});

// [info]: methods

const handleCloseModal = () => {
    emits("handleCloseModal");
};

const setActiveCannedResponse = (cannedResponse) => {
    if (!cannedResponse) return;
    activeCannedResponses.value = cannedResponse;
};
const getCannedResponses = async (searchQuery, isSilent) => {
    try {
        if (!isSilent) loadingStates.value.list = true;
        const resp = await getCannedResponseList(searchQuery || "");
        cannedResponses.value = resp.data;
        if (resp.data.list.length > 0) {
            activeCannedResponses.value = resp.data.list[0];
        } else {
            activeCannedResponses.value = {};
        }
    } catch (err) {
        console.error("[ERR]: at getCannedResponses", err);
    } finally {
        loadingStates.value.list = false;
    }
};

const searchDebounced = useDebounceFn((e) => getCannedResponses(e, true), 300);

const handleSelectCannedResponse = () => {
    if (activeCannedResponses.value.detail_info) {
        emits("handleSelectCannedResponse", activeCannedResponses.value.detail_info);
    }
};

// [info]: lifecycle
onMounted(() => {
    getCannedResponses();
});
</script>

<style scoped>
:deep(.canned_message .text_container img) {
    height: 200px;
    padding: 2px;
    object-fit: cover;
}
</style>
